import { Vue, Options, Watch } from 'vue-property-decorator';
import { namespace } from 's-vuex-class';
import UserSettings from './components/UserSettings/index.vue';
import ChangePasswordForm from './components/ChangePasswordForm/index.vue';
import { User, UserTypeEnum } from '@/types';
import { ConversationInfo } from '@/pages/Start/types';
import { NotificationState } from '@/store/modules/notification/types';
import { getIoInstance } from '@/socket';
import { cancelPlacement, handleReassign, isPlacing, settlePlacement } from '@/socket/placement';

/**
 * Placements that land on the first instance settle in well under this, so
 * waiting it out keeps the overlay off screen entirely for hearing users (who
 * never hop) and for signers who get the local slot.
 */
const overlayDelayMs = 400;

/**
 * A placement normally finishes inside ~5s. Past this it is worth offering a
 * way out rather than leaving the user with no control.
 */
const cancelDelayMs = 10_000;

const chat = namespace('chat');
const app = namespace('app');
const notification = namespace('notification');

@Options({
  components: {
    UserSettings,
    ChangePasswordForm,
  },
})
export default class SocketConnectionLayout extends Vue {
  @chat.State private readonly conversationInfo!: ConversationInfo;
  @chat.Getter private readonly me!: User;
  @chat.Mutation private readonly userSignedOut!: () => void;
  @chat.Action private readonly loadUserFromStorage!: () => void;
  @chat.Action private readonly clearGuestUser!: () => void;
  @chat.Action private readonly setConversationInfo!: (info: ConversationInfo) => void;
  @chat.Action private readonly clearConversationInfo!: () => void;

  @app.State public isShowUserSettings!: boolean;
  @app.State public isShowChangePassword!: boolean;
  @app.State public isShowSignOutDialog!: boolean;
  @app.Action public setIsShowUserSettings!: (value: boolean) => void;
  @app.Action public setIsShowChangePassword!: (value: boolean) => void;
  @app.Action public setIsShowSignOutDialog!: (value: boolean) => void;

  @notification.Action private readonly initNotification!: (
    data: Partial<NotificationState>,
  ) => void;

  public isSocketLoading = false;

  /**
   * Rendered here rather than on the pages because a placement outlives them:
   * the reconnect between hops can unmount whichever page asked, and the same
   * overlay has to cover joining, starting and rejoining.
   */
  public isPlacementVisible = false;
  public isPlacementCancelVisible = false;
  private placementShowTimer: number | null = null;
  private placementCancelTimer: number | null = null;

  public get isFooterVisible() {
    return ['start', 'resetPassword'].includes(String(this.$route.name));
  }

  public get isPlacing(): boolean {
    return isPlacing.value;
  }

  @Watch('isPlacing')
  private onPlacingChanged(placing: boolean) {
    this.clearPlacementTimers();

    if (!placing) {
      this.isPlacementVisible = false;
      this.isPlacementCancelVisible = false;
      return;
    }

    this.placementShowTimer = window.setTimeout(() => {
      this.isPlacementVisible = true;
    }, overlayDelayMs);
    this.placementCancelTimer = window.setTimeout(() => {
      this.isPlacementCancelVisible = true;
    }, cancelDelayMs);
  }

  private clearPlacementTimers() {
    if (this.placementShowTimer !== null) {
      window.clearTimeout(this.placementShowTimer);
      this.placementShowTimer = null;
    }
    if (this.placementCancelTimer !== null) {
      window.clearTimeout(this.placementCancelTimer);
      this.placementCancelTimer = null;
    }
  }

  public handlePlacementCancel() {
    cancelPlacement();
  }

  beforeMount() {
    this.loadUserFromStorage();

    let token = '';

    if (this.me?.token || this.$route.name === 'conversation') {
      token = this.me?.token;
    }

    getIoInstance().connect({
      token,
    });
  }

  mounted() {
    this.sockets.subscribe('signedOut', () => {
      this.reconnectUser();
    });

    this.sockets.subscribe('setAppSettings', () => {
      getIoInstance().setIsConnected(true);
    });

    // Reassignment can answer a join, a start or a rejoin, and the reconnect it
    // triggers outlives the page that asked. Handled once here, where the
    // subscription lasts as long as the socket does.
    this.sockets.subscribe('reassign', () => handleReassign());

    const endConversation = () => {
      const info = { ...this.conversationInfo };
      this.setConversationInfo(info);
      setTimeout(() => {
        this.$router.push({
          name: 'start',
          state: {
            showPendoGuide: this.me.userMode === UserTypeEnum.du,
          },
        });
      });
    };

    this.sockets.subscribe('conversationStopped', endConversation);
    this.sockets.subscribe('roomStopped', (userId: string) => {
      if (this.me.id !== userId) {
        this.initNotification({ message: 'Room was stopped.', mode: 'info' });
      }

      if (!this.me?.isAuthorized) {
        this.reconnectUser();
      }
      this.clearGuestUser();

      if (this.$route.name === 'conversation') {
        endConversation();
      }

      this.clearConversationInfo();
    });

    // The server dropped this user from a room whose recognition model it no
    // longer serves. The answer to a pending rejoin is final, so stop waiting
    // for a 'userJoined' and leave Start offering a new conversation.
    this.sockets.subscribe('conversationModelExpired', () => {
      settlePlacement();
      this.clearConversationInfo();
    });

    this.sockets.subscribe('userUpdated', (user: User) => {
      getIoInstance().updateQuery({
        token: user?.token,
      });
    });
  }

  unmounted() {
    this.clearPlacementTimers();
    this.sockets.unsubscribe('setAppSettings');
    this.sockets.unsubscribe('reassign');
    this.sockets.unsubscribe('signedOut');
    this.sockets.unsubscribe('roomStopped');
    this.sockets.unsubscribe('conversationStopped');
    this.sockets.unsubscribe('conversationModelExpired');
    this.sockets.unsubscribe('userUpdated');
    getIoInstance().disconnect();
  }

  get firstName() {
    return this.me?.firstName;
  }

  private reconnectUser() {
    getIoInstance().disconnect();
    getIoInstance().connect({
      token: '',
    });
  }

  public handleClickSignOut() {
    this.$socket.emit('signOut');
    localStorage.removeItem('user');
    this.userSignedOut();
    this.setIsShowSignOutDialog(false);
  }

  public callIfConnected(func: () => void) {
    getIoInstance().callIfConnected(func);
  }
}
